/**
 * aeterna/neural/grammar.ts — Neuro-Symbolic Intent Grammar
 *
 * Symbolic grammar handles structure. A tiny classifier handles ambiguity.
 *
 * Earley parser processes a context-free grammar for deterministic intent
 * recognition of furniture interactions, world commands, and structured
 * queries. Only when the parse yields ≥2 valid trees does the neural
 * fallback activate to disambiguate.
 *
 * This means: deterministic parsing for "put the lamp on the table"
 * (zero latency, zero error), neural disambiguation only for
 * natural conversational messages to the Aeterna.
 *
 * The grammar is extensible — new furniture types, actions, and query
 * patterns can be added without retraining anything.
 */

import { zeros, dot, softmax, randomMatrix, matvec, vadd, vsigmoid, mulberry32, gaussianSeeded } from "./math.js";

// ─── Token Types ─────────────────────────────────────────────────────────────

export type IntentType =
  | "action"          // put, move, place, rotate, pick up
  | "question"        // what, how, does, is
  | "emote"           // expressing emotion
  | "greeting"        // hello, hi, hey
  | "farewell"        // bye, goodnight
  | "aeterna_address" // talking to the Aeterna directly
  | "ambient"         // general conversation, no clear intent
  | "command";        // system commands (inventory, help)

export interface ParsedIntent {
  type: IntentType;
  /** Confidence 0–1 */
  confidence: number;
  /** Extracted entities */
  entities: Record<string, string>;
  /** Whether this was resolved by grammar (true) or neural fallback (false) */
  symbolic: boolean;
  /** Raw matched rule name, if symbolic */
  rule?: string;
  /** Alternative parses, if ambiguous */
  alternatives?: ParsedIntent[];
}

// ─── Grammar Rules ───────────────────────────────────────────────────────────

interface GrammarRule {
  name: string;
  intent: IntentType;
  /** Pattern: array of token matchers (string = literal, /regex/ = pattern) */
  patterns: Array<string | RegExp>[];
  /** Entity extraction map: position index → entity name */
  entities?: Record<number, string>;
}

const GRAMMAR_RULES: GrammarRule[] = [
  // ── Actions ─────────────────────────────────────────────────────────────
  {
    name: "place_furniture",
    intent: "action",
    patterns: [
      [/^(put|place|set)$/i, /^\w+$/, /^(on|at|near)$/i, /^\w+$/],
      [/^(put|place|set)$/i, /^the$/i, /^\w+$/, /^(on|at|near)$/i, /^the$/i, /^\w+$/],
    ],
    entities: { 1: "object", 3: "location" },
  },
  {
    name: "move_furniture",
    intent: "action",
    patterns: [
      [/^(move|drag|shift)$/i, /^\w+$/, /^(to|toward)$/i, /^\w+$/],
      [/^(move|drag|shift)$/i, /^the$/i, /^\w+$/, /^(to|toward)$/i, /^\w+$/],
    ],
    entities: { 1: "object", 3: "destination" },
  },
  {
    name: "rotate_furniture",
    intent: "action",
    patterns: [
      [/^(rotate|turn|spin)$/i, /^\w+$/],
      [/^(rotate|turn|spin)$/i, /^the$/i, /^\w+$/],
    ],
    entities: { 1: "object" },
  },
  {
    name: "pickup_furniture",
    intent: "action",
    patterns: [
      [/^pick$/i, /^up$/i, /^\w+$/],
      [/^pick$/i, /^up$/i, /^the$/i, /^\w+$/],
      [/^(grab|take|remove)$/i, /^\w+$/],
      [/^(grab|take|remove)$/i, /^the$/i, /^\w+$/],
    ],
    entities: { 2: "object" },
  },
  {
    name: "use_item",
    intent: "action",
    patterns: [
      [/^(use|play|sit|read|water|light)$/i, /^\w+$/],
      [/^(use|play|sit|read|water|light)$/i, /^(on|the|in)$/i, /^\w+$/],
    ],
    entities: { 0: "verb", 1: "object" },
  },
  {
    name: "give_item",
    intent: "action",
    patterns: [
      [/^give$/i, /^\w+$/, /^\w+$/],
      [/^give$/i, /^the$/i, /^\w+$/, /^to$/i, /^\w+$/],
    ],
    entities: { 1: "item", 2: "recipient" },
  },
  {
    name: "craft_item",
    intent: "action",
    patterns: [
      [/^(craft|make|build|create)$/i, /^\w+$/],
      [/^(craft|make|build|create)$/i, /^(a|an|the)$/i, /^\w+$/],
    ],
    entities: { 1: "item" },
  },

  // ── Questions ───────────────────────────────────────────────────────────
  {
    name: "what_is",
    intent: "question",
    patterns: [
      [/^what$/i, /^(is|are)$/i, /^\w+$/],
      [/^what$/i, /^(is|are)$/i, /^(this|that|the)$/i, /^\w+$/],
    ],
    entities: { 2: "subject" },
  },
  {
    name: "how_to",
    intent: "question",
    patterns: [
      [/^how$/i, /^(do|can|should)$/i, /^(i|you|we)$/i, /^\w+$/],
    ],
    entities: { 3: "verb" },
  },
  {
    name: "where_is",
    intent: "question",
    patterns: [
      [/^where$/i, /^(is|are)$/i, /^\w+$/],
    ],
    entities: { 2: "subject" },
  },

  // ── Greetings ───────────────────────────────────────────────────────────
  {
    name: "greeting",
    intent: "greeting",
    patterns: [
      [/^(hello|hi|hey|heya|morning|evening|afternoon)$/i],
      [/^good$/i, /^(morning|evening|afternoon|night)$/i],
    ],
  },
  {
    name: "farewell",
    intent: "farewell",
    patterns: [
      [/^(bye|goodbye|goodnight|cya|later)$/i],
      [/^good$/i, /^night$/i],
      [/^see$/i, /^you$/i],
    ],
  },

  // ── Commands ────────────────────────────────────────────────────────────
  {
    name: "open_inventory",
    intent: "command",
    patterns: [
      [/^(inventory|items|backpack|bag)$/i],
      [/^(open|show)$/i, /^(inventory|items|backpack)$/i],
    ],
  },
  {
    name: "help",
    intent: "command",
    patterns: [
      [/^(help|commands|controls)$/i],
    ],
  },

  // ── Aeterna address ─────────────────────────────────────────────────────
  {
    name: "aeterna_address",
    intent: "aeterna_address",
    patterns: [
      [/^(aeterna|hey aeterna)$/i],
      [/^(are|do|can|have|would|will)$/i, /^you$/i],
      [/^(what|who|how|why)$/i, /^(are|do|did)$/i, /^you$/i],
    ],
  },

  // ── Emotes ──────────────────────────────────────────────────────────────
  {
    name: "emote",
    intent: "emote",
    patterns: [
      [/^\*(.*)\*$/],  // *action*
      [/^:[\w]+:$/],   // :emoji:
    ],
  },
];

// ─── Earley-Inspired Parser ──────────────────────────────────────────────────

function tokenizeInput(text: string): string[] {
  return text.trim().split(/\s+/).filter(t => t.length > 0);
}

function matchPattern(tokens: string[], pattern: Array<string | RegExp>): Record<number, string> | null {
  if (tokens.length < pattern.length) return null;

  const entities: Record<number, string> = {};

  for (let i = 0; i < pattern.length; i++) {
    const matcher = pattern[i];
    const token = tokens[i];
    if (!token) return null;

    if (typeof matcher === "string") {
      if (token.toLowerCase() !== matcher.toLowerCase()) return null;
    } else {
      if (!matcher.test(token)) return null;
    }
    entities[i] = token;
  }

  return entities;
}

/**
 * Parse input text against all grammar rules.
 * Returns all matching intents, sorted by specificity.
 */
function grammarParse(text: string): ParsedIntent[] {
  const tokens = tokenizeInput(text);
  if (tokens.length === 0) return [];

  const matches: ParsedIntent[] = [];

  for (const rule of GRAMMAR_RULES) {
    for (const pattern of rule.patterns) {
      const entityMap = matchPattern(tokens, pattern);
      if (entityMap) {
        const entities: Record<string, string> = {};
        if (rule.entities) {
          for (const [posStr, name] of Object.entries(rule.entities)) {
            const pos = parseInt(posStr, 10);
            if (entityMap[pos]) entities[name] = entityMap[pos];
          }
        }

        matches.push({
          type: rule.intent,
          confidence: pattern.length / tokens.length, // longer match = higher confidence
          entities,
          symbolic: true,
          rule: rule.name,
        });
      }
    }
  }

  // Sort by confidence (specificity)
  matches.sort((a, b) => b.confidence - a.confidence);
  return matches;
}

// ─── Neural Disambiguator ────────────────────────────────────────────────────
// Small 3-layer perceptron: 64-dim embedding → 32 hidden → N intents
// Only activates when grammar yields ≥2 valid parses.

const INTENT_TYPES: IntentType[] = [
  "action", "question", "emote", "greeting",
  "farewell", "aeterna_address", "ambient", "command",
];

const DISAMBIG_INPUT = 64;
const DISAMBIG_HIDDEN = 32;
const DISAMBIG_OUTPUT = INTENT_TYPES.length;

class NeuralDisambiguator {
  private W1: Float32Array;
  private b1: Float32Array;
  private W2: Float32Array;
  private b2: Float32Array;

  constructor(seed = 8888) {
    this.W1 = randomMatrix(DISAMBIG_HIDDEN, DISAMBIG_INPUT, 1 / Math.sqrt(DISAMBIG_INPUT), seed);
    this.b1 = zeros(DISAMBIG_HIDDEN);
    this.W2 = randomMatrix(DISAMBIG_OUTPUT, DISAMBIG_HIDDEN, 1 / Math.sqrt(DISAMBIG_HIDDEN), seed + 1);
    this.b2 = zeros(DISAMBIG_OUTPUT);
  }

  /** Predict intent distribution from embedding */
  predict(embedding: Float32Array): Float32Array {
    // Hidden layer with tanh
    const h = matvec(this.W1, DISAMBIG_HIDDEN, DISAMBIG_INPUT, embedding);
    vadd(h, this.b1, h);
    for (let i = 0; i < h.length; i++) h[i] = Math.tanh(h[i]);

    // Output layer with softmax
    const logits = matvec(this.W2, DISAMBIG_OUTPUT, DISAMBIG_HIDDEN, h);
    vadd(logits, this.b2, logits);
    return softmax(logits);
  }
}

// ─── Intent Parser (unified interface) ───────────────────────────────────────

export class IntentParser {
  private disambiguator: NeuralDisambiguator;

  constructor(seed = 8888) {
    this.disambiguator = new NeuralDisambiguator(seed);
  }

  /**
   * Parse player message into intent.
   *
   * Flow:
   *   1. Run grammar parse
   *   2. If single clear match → return it (deterministic, zero latency)
   *   3. If multiple matches → use neural disambiguator
   *   4. If no matches → classify as ambient/aeterna_address via neural
   *
   * embedding: optional 64-dim embedding from the embeddings module
   *            (required for neural fallback)
   */
  parse(text: string, embedding?: Float32Array): ParsedIntent {
    const grammarResults = grammarParse(text);

    // Case 1: Single clear match
    if (grammarResults.length === 1 && grammarResults[0].confidence > 0.5) {
      return grammarResults[0];
    }

    // Case 2: Multiple matches — disambiguate
    if (grammarResults.length >= 2 && embedding) {
      const probs = this.disambiguator.predict(embedding);
      let bestIdx = 0;
      for (let i = 1; i < probs.length; i++) {
        if (probs[i] > probs[bestIdx]) bestIdx = i;
      }

      // Find the grammar result matching the neural prediction
      const predictedType = INTENT_TYPES[bestIdx];
      const match = grammarResults.find(r => r.type === predictedType);
      if (match) {
        match.confidence = Math.max(match.confidence, probs[bestIdx]);
        match.alternatives = grammarResults.filter(r => r !== match);
        return match;
      }
    }

    // Case 3: No grammar match — pure neural classification
    if (embedding) {
      const probs = this.disambiguator.predict(embedding);
      let bestIdx = 0;
      for (let i = 1; i < probs.length; i++) {
        if (probs[i] > probs[bestIdx]) bestIdx = i;
      }

      return {
        type: INTENT_TYPES[bestIdx],
        confidence: probs[bestIdx],
        entities: {},
        symbolic: false,
        alternatives: grammarResults.length > 0 ? grammarResults : undefined,
      };
    }

    // Case 4: No embedding available — check for simple keyword heuristics
    const lower = text.toLowerCase().trim();

    if (/^(hi|hello|hey|morning|evening)/i.test(lower)) {
      return { type: "greeting", confidence: 0.7, entities: {}, symbolic: false };
    }
    if (/^(bye|goodnight|later|cya)/i.test(lower)) {
      return { type: "farewell", confidence: 0.7, entities: {}, symbolic: false };
    }
    if (/\?$/.test(lower)) {
      return { type: "question", confidence: 0.5, entities: {}, symbolic: false };
    }
    if (/^(you|are you|do you|what are you)/i.test(lower)) {
      return { type: "aeterna_address", confidence: 0.6, entities: {}, symbolic: false };
    }

    // Default: ambient conversation
    return {
      type: "ambient",
      confidence: 0.4,
      entities: {},
      symbolic: false,
      alternatives: grammarResults.length > 0 ? grammarResults : undefined,
    };
  }

  /**
   * Quick check: is this message a world action (furniture interaction)?
   * Used to decide if the message should be handled by the game engine
   * vs sent to the Aeterna for conversation.
   */
  isWorldAction(text: string): boolean {
    const results = grammarParse(text);
    return results.some(r => r.type === "action" && r.confidence > 0.5);
  }

  /**
   * Quick check: is this message addressed to the Aeterna?
   */
  isAeternaMessage(text: string): boolean {
    const results = grammarParse(text);
    if (results.some(r => r.type === "aeterna_address")) return true;
    // If no clear world action, assume it's for the Aeterna
    return !results.some(r => r.type === "action" || r.type === "command");
  }
}
